import { Session } from '@ory/client/api';
import { signal } from '@preact/signals-react';
import { ConsentStatusEnum } from '@pypestream/api-services';
import { Page, PageBody, Spinner } from '@pypestream/design-system';
import * as Sentry from '@sentry/react';
import { Replay } from '@sentry/react';
import React, { useEffect } from 'react';

import { removeTrailingSlash } from '@pypestream/utils';
import { kratosApi } from './kratos.api';
import { useAnalytics } from './hooks';
import {
  sendGlobalAppEvent,
  sendUserEvent,
  useManagerCtxSelector,
  useUserCtxSelector,
} from './xstate/app.xstate';

export type WindowProduktly = Window &
  typeof globalThis & {
    Produktly: {
      identifyUser: (
        userId?: string,
        metadata?: Record<string, unknown>
      ) => void;
    };
  };

const session = signal<Session | null>(null);

kratosApi
  .toSession()
  .then(({ data }) => {
    session.value = data;

    sendGlobalAppEvent({
      type: 'user.signIn',
      user: {
        // I'm trying to avoid passing in this info, only to have it immediately get overwritten
        // by our getCurrentUser GQL query
        //
        // email: data.identity.traits.email,
        // firstName: data.identity.traits.firstName,
        // lastName: data.identity.traits.lastName,
        // profilePhoto: data.identity.traits.picture,
        kratosId: data.identity.id,
      },
    });
  })
  .catch((error) => {
    console.error('Session error:', JSON.stringify(error));
    sendUserEvent('user.signOut');
  });

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const analytics = useAnalytics();

  const { pathname } = window.location;

  const pathAsArray = removeTrailingSlash(pathname).split('/').filter(Boolean);
  const isHomePage = pathname === '/' || pathAsArray.length === 2;

  const { optionalConsentStatus, userId, userEmail, userName } =
    useUserCtxSelector((ctx) => ({
      optionalConsentStatus: ctx.user?.optionalConsentStatus,
      userId: ctx.user?.id,
      userEmail: ctx.user?.email,
      userName: `${ctx.user?.firstName} ${ctx.user?.lastName}`,
    }));

  const { enablePostHogSessionRecording } = useManagerCtxSelector((ctx) => ({
    enablePostHogSessionRecording:
      ctx.featureFlags?.['post-hog-session-recording'] ?? false,
  }));

  useEffect(() => {
    if (!optionalConsentStatus || !analytics) {
      if (!optionalConsentStatus) {
        console.log('>>>>>>> Analytics: Waiting for consent status');
      }
      if (!analytics) {
        console.log('>>>>>>> Analytics: Analytics instance not available');
      }
      return;
    }

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      // Check if analytics is fully loaded before proceeding
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isLoaded = (analytics as any).__loaded;
      let sessionRecordingConfig: unknown = 'unknown';
      try {
        if (typeof analytics.get_config === 'function') {
          sessionRecordingConfig =
            analytics.get_config('session_recording') || 'unknown';
        }
      } catch (e) {
        // get_config not available
      }

      if (typeof analytics.opt_in_capturing === 'function') {
        analytics.opt_in_capturing();
      }

      if (userId && typeof analytics.identify === 'function') {
        analytics.identify(userId, {
          email: userEmail,
          name: userName,
          app: 'manager',
        });
      }
    } else {
      if (typeof analytics.opt_out_capturing === 'function') {
        analytics.opt_out_capturing();
      }
    }
  }, [optionalConsentStatus, analytics, userEmail, userId, userName]);

  useEffect(() => {
    if (!optionalConsentStatus) return;

    const sentryReplay = Sentry.getCurrentHub()
      .getClient()
      ?.getIntegration(Replay);

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      sentryReplay?.start();
      (window as WindowProduktly).Produktly.identifyUser(userId, {
        email: userEmail,
        name: userName,
      });
      console.log('start capturing');
    } else {
      sentryReplay?.stop();
      Sentry.close().then(function () {
        console.log('opting out of Sentry');
      });
    }
  }, [optionalConsentStatus, userEmail, userId, userName]);

  useEffect(() => {
    if (
      !analytics ||
      typeof analytics.startSessionRecording !== 'function' ||
      typeof analytics.stopSessionRecording !== 'function' ||
      typeof analytics.sessionRecordingStarted !== 'function'
    )
      return;

    if (
      optionalConsentStatus === ConsentStatusEnum.Accepted &&
      enablePostHogSessionRecording &&
      typeof analytics.startSessionRecording === 'function' &&
      typeof analytics.stopSessionRecording === 'function'
    ) {
      if (typeof analytics.startSessionRecording === 'function') {
        analytics.startSessionRecording();
      }
    } else {
      if (typeof analytics.stopSessionRecording === 'function') {
        analytics.stopSessionRecording();
      }
    }
  }, [enablePostHogSessionRecording, optionalConsentStatus, analytics]);

  // TODO remove if not needed (should not show white page without it)
  if (!session.value) {
    return (
      <Page>
        <PageBody background="none">
          <Spinner fullpage />
        </PageBody>
      </Page>
    );
  }

  // todo: do we need this?
  // const splittedPath = window.location.href.split('?');
  // window.history.pushState('authenticated', '', splittedPath[0]);
  return <>{children}</>;
};
